import type {
  DashboardLineView,
  DashboardSort,
  DashboardStatusFilter,
} from "@/types/dashboard.types";

export const SORT_OPTIONS: Array<{ id: DashboardSort; label: string }> = [
  { id: "name", label: "Nome (A–Z)" },
  { id: "oee-desc", label: "OEE (maior primeiro)" },
  { id: "oee-asc", label: "OEE (menor primeiro)" },
  { id: "plan", label: "Aderência ao plano" },
  { id: "status", label: "Situação" },
];

export interface StatusFilterDef {
  id: DashboardStatusFilter;
  label: string;
  match: (line: DashboardLineView) => boolean;
}

export const STATUS_FILTERS: StatusFilterDef[] = [
  { id: "todas", label: "Todas", match: () => true },
  { id: "producao", label: "Em produção", match: (l) => l.status === "RUNNING" },
  { id: "pausada", label: "Pausada", match: (l) => l.status === "PARTIAL" },
  { id: "alarme", label: "Com alarme", match: (l) => l.activeAlarms > 0 },
];

export interface FilterCriteria {
  statusFilter: DashboardStatusFilter;
  query: string;
  responsibleFilter: Set<string>;
}

export function filterLines<T extends DashboardLineView>(lines: T[], c: FilterCriteria): T[] {
  const def = STATUS_FILTERS.find((f) => f.id === c.statusFilter);
  const q = c.query.trim().toLowerCase();
  return lines.filter((l) => {
    if (def && !def.match(l)) return false;
    if (
      c.responsibleFilter.size > 0 &&
      !(l.responsible && c.responsibleFilter.has(l.responsible))
    ) {
      return false;
    }
    if (q) {
      const haystack = `${l.name} ${l.description ?? ""} ${l.product ?? ""}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

export function sortLines<T extends DashboardLineView>(lines: T[], sort: DashboardSort): T[] {
  const arr = [...lines];
  switch (sort) {
    case "name":
      return arr.sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    case "status":
      return arr.sort((a, b) => a.status.localeCompare(b.status));
    case "oee-desc":
      return arr.sort((a, b) => (b.oee ?? -Infinity) - (a.oee ?? -Infinity));
    case "oee-asc":
      return arr.sort((a, b) => (a.oee ?? Infinity) - (b.oee ?? Infinity));
    case "plan":
      return arr.sort((a, b) => (a.planAdherence ?? Infinity) - (b.planAdherence ?? Infinity));
    default:
      return arr;
  }
}

export function statusCounts(lines: DashboardLineView[]): Record<DashboardStatusFilter, number> {
  return STATUS_FILTERS.reduce(
    (acc, f) => {
      acc[f.id] = lines.filter(f.match).length;
      return acc;
    },
    {} as Record<DashboardStatusFilter, number>
  );
}
